import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Clock, Camera, Mail, AlertTriangle, Droplets, SprayCan, Thermometer, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import OrderTimeline from '../components/OrderTimeline';
import { playOrderSuccess } from '../utils/sound';

const API = `${window.location.origin}/api`;

const STATUS_COLORS = {
  'Pending Payment': 'text-yellow-400',
  'Paid': 'text-blue-400',
  'Preparing': 'text-purple-400',
  'Shipped': 'text-cyan-400',
  'Completed': 'text-green-400',
  'Rejected': 'text-red-400',
};

const QUICK_CARE_TIPS = [
  { icon: Droplets, tip: "Apply on pulse points — wrists, neck, behind ears" },
  { icon: SprayCan, tip: "Don't rub, let it air dry for best projection" },
  { icon: Thermometer, tip: "Store in cool, dry place away from sunlight" },
  { icon: Clock, tip: "Can't smell it after a while? That's nose fatigue — others still can" },
];

export default function OrderConfirmationPage() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders/${orderNumber}`).then(res => {
      setOrder(res.data);
      setLoading(false);
      // Celebration chime once order is loaded
      setTimeout(() => playOrderSuccess(), 250);
    }).catch(() => setLoading(false));
  }, [orderNumber]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/40">Loading order...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 text-lg mb-4">Order not found</p>
          <Link to="/" className="text-[#D4AF37] text-sm uppercase tracking-widest">Go Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="order-confirmation-page" className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

        {/* Thank You Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <CheckCircle className="w-9 h-9 text-[#D4AF37]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Thank You!
          </h1>
          <p className="text-white/50 text-sm font-sans">Your order has been placed successfully.</p>
        </div>

        {/* Order Number — Prominent */}
        <div className="bg-[#141414] border border-[#D4AF37]/30 p-6 text-center mb-5" data-testid="order-number-card">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2 font-sans">Order Number</p>
          <p className="text-2xl text-[#D4AF37] font-mono font-semibold tracking-wide" data-testid="order-number">{order.order_number}</p>
        </div>

        {/* Screenshot Reminder */}
        <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-4 mb-5 flex items-start gap-3" data-testid="screenshot-reminder">
          <Camera className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm font-sans font-medium mb-0.5">Screenshot this page</p>
            <p className="text-white/40 text-xs font-sans leading-relaxed">
              Save a screenshot of this page as your proof of order. You'll need your order number for tracking and reference.
            </p>
          </div>
        </div>

        {/* Check Email / Spam Warning */}
        <div className="bg-[#1a1a1a] border border-white/5 p-4 mb-5 flex items-start gap-3" data-testid="email-spam-warning">
          <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm font-sans font-medium mb-0.5">Check your email</p>
            <p className="text-white/40 text-xs font-sans leading-relaxed">
              We've sent a confirmation to <span className="text-white/60">{order.email || order.customer_email || 'your email'}</span>.
              If you don't see it, please <span className="text-yellow-400/80">check your Spam or Junk folder</span>.
              Our emails sometimes land there — just mark it as "Not Spam" to receive future updates.
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="bg-[#141414] border border-white/5 p-4 mb-5 flex items-center gap-3" data-testid="order-status-card">
          <Clock className="w-5 h-5 text-white/40" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 font-sans">Status</p>
            <p className={`text-sm font-sans font-medium ${STATUS_COLORS[order.status] || 'text-white'}`} data-testid="order-status">
              {order.status}
            </p>
          </div>
        </div>

        {/* Order Tracking Timeline */}
        <div className="mb-5">
          <OrderTimeline status={order.status} statusHistory={order.status_history || []} />
        </div>

        {/* Items Ordered — Collapsible */}
        <div className="bg-[#141414] border border-white/5 mb-5" data-testid="order-items">
          <button
            onClick={() => setShowItems(!showItems)}
            className="w-full flex items-center justify-between p-4 text-left"
            data-testid="toggle-items-btn"
          >
            <h3 className="text-xs uppercase tracking-[0.15em] text-[#D4AF37] font-sans font-medium">
              Items Ordered ({order.items?.length || 0})
            </h3>
            {showItems ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
          </button>
          {showItems && (
            <div className="px-4 pb-4">
              {order.items?.map((item, i) => (
                <div key={i} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm font-light">{item.product_name} <span className="text-white/40">x{item.quantity}</span></p>
                    {item.variant && <p className="text-white/30 text-xs">{item.variant.size} &middot; {item.variant.type}</p>}
                  </div>
                  <p className="text-white/60 text-sm font-sans">₱{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              ))}
              <div className="pt-3 border-t border-white/10 mt-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-white/40 text-sm font-sans">Subtotal</span>
                  <span className="text-white/60 font-sans text-sm">₱{order.subtotal?.toLocaleString()}</span>
                </div>
                {order.shipping_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-white/40 text-sm font-sans">Shipping</span>
                    <span className="text-white/60 font-sans text-sm">₱{order.shipping_fee?.toLocaleString()}</span>
                  </div>
                )}
                {order.shipping_fee === 0 && order.shipping_region && (
                  <div className="flex justify-between">
                    <span className="text-white/40 text-sm font-sans">Shipping</span>
                    <span className="text-green-400 font-sans text-sm">Free</span>
                  </div>
                )}
                <div className="flex justify-between pt-1 border-t border-white/5">
                  <span className="text-white text-sm font-sans font-medium">Total</span>
                  <span className="text-[#D4AF37] font-sans font-semibold">₱{order.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Customer & Payment Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div className="bg-[#141414] border border-white/5 p-4" data-testid="customer-details">
            <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] mb-2 font-sans font-medium">Delivery</h3>
            <p className="text-white text-sm">{order.customer_name}</p>
            <p className="text-white/50 text-xs">{order.phone}</p>
            <p className="text-white/40 text-xs mt-1">
              {order.address?.house}, {order.address?.barangay}, {order.address?.city}, {order.address?.province} {order.address?.zip}
            </p>
          </div>
          <div className="bg-[#141414] border border-white/5 p-4" data-testid="payment-info">
            <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] mb-2 font-sans font-medium">Payment</h3>
            <p className="text-white text-sm capitalize">{order.payment_method?.replace('_', ' ')}</p>
            {order.tracking_number && (
              <div className="mt-2 pt-2 border-t border-white/5">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-sans">Tracking</p>
                <p className="text-white font-mono text-xs">{order.tracking_number}</p>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Estimate */}
        <div className="bg-[#141414] border border-[#D4AF37]/10 p-4 mb-8 text-center" data-testid="delivery-estimate">
          <p className="text-white/60 text-sm font-sans">
            Estimated delivery: <span className="text-white font-medium">3-7 business days</span>
          </p>
        </div>

        {/* After Care Guide Preview */}
        <div className="mb-8" data-testid="after-care-section">
          <div className="text-center mb-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-sans mb-1">Fragrance Tips</p>
            <h3 className="text-lg text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              After Care Guide
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QUICK_CARE_TIPS.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="bg-[#141414] border border-white/5 p-4 text-center" data-testid={`quick-care-tip-${i}`}>
                  <Icon className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-white/50 text-xs font-sans leading-relaxed">{t.tip}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-3">
            <Link
              to="/after-care"
              className="text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs font-sans uppercase tracking-wider transition-colors"
              data-testid="full-care-guide-link"
            >
              View Full After Care Guide &rarr;
            </Link>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/shop"
            className="flex-1 text-center bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Link>
          <Link
            to="/track-order"
            className="flex-1 text-center border border-[#D4AF37]/40 text-[#D4AF37] py-3 text-sm uppercase tracking-[0.15em] hover:border-[#D4AF37] transition-colors"
            data-testid="track-order-btn"
          >
            Track Your Order
          </Link>
        </div>
      </div>
    </div>
  );
}
